import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Header } from "@/pages/CollegePredictorPage";
import { Gift, Copy, Check, Users, Crown, Share2, Sparkles } from "lucide-react";

export default function ReferralsPage() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api.get("/referrals").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="p-8 text-slate-400">Loading...</div>;

  const link = `${window.location.origin}/auth?ref=${data.code}`;
  const copy = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — please copy it manually.");
    }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Join EntranceZone AI", text: "Get free AI career guidance!", url: link }); } catch {}
    } else copy(link);
  };

  const completed = data.referred.filter((r) => r.completed).length;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-8 py-8">
      <Header icon={Gift} eyebrow="Refer & Earn"
        title="Invite friends, earn Premium days" desc={`Get ${data.bonus_per_referral} bonus Premium days each time a friend completes their assessment.`} />

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Stat icon={Users} label="Friends Referred" value={data.referral_count} color="cyan" />
        <Stat icon={Crown} label="Bonus Premium Days" value={data.bonus_premium_days} color="amber" testid="referral-bonus-days" />
        <Stat icon={Sparkles} label="Current Plan" value={data.plan} color="emerald" caps />
      </div>

      {/* Share card */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-cyan-400/25 bg-gradient-to-br from-[#0F2A33] to-[#131C2E] p-6">
        <p className="text-sm font-semibold text-white mb-1">Your referral code</p>
        <p className="text-xs text-slate-400 mb-4">Share your link — when a friend signs up and finishes the assessment, you both win.</p>

        <div className="flex flex-wrap items-center gap-3">
          <div data-testid="referral-code" className="rounded-xl border border-white/10 bg-[#0B0F17] px-5 py-3 font-mono-data text-xl font-bold tracking-widest text-cyan-300">
            {data.code}
          </div>
          <button data-testid="copy-code-btn" onClick={() => copy(data.code)}
            className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm font-semibold text-white hover:bg-white/[0.06]">
            {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />} Copy code
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px] truncate rounded-xl border border-white/10 bg-[#0B0F17] px-4 py-3 text-sm text-slate-400">
            {link}
          </div>
          <button data-testid="copy-link-btn" onClick={() => copy(link)}
            className="inline-flex items-center gap-2 rounded-xl bg-cyan-400 px-4 py-3 text-sm font-bold text-[#041016] transition-transform hover:scale-105">
            <Copy className="h-4 w-4" /> Copy link
          </button>
          <button data-testid="share-link-btn" onClick={share}
            className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-3 text-sm font-bold text-cyan-300">
            <Share2 className="h-4 w-4" /> Share
          </button>
        </div>
      </motion.div>

      {/* Referred list */}
      <div className="mt-8 rounded-2xl border border-white/8 bg-[#131C2E] p-6">
        <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-cyan-400" /> Your invites ({completed}/{data.referred.length} completed)
        </h3>
        {data.referred.length === 0 ? (
          <p className="text-sm text-slate-400">No invites yet. Share your link to start earning Premium days!</p>
        ) : (
          <div className="space-y-2">
            {data.referred.map((r, i) => (
              <div key={i} data-testid="referral-invite-row" className="flex items-center justify-between rounded-xl bg-white/[0.02] px-4 py-3">
                <span className="text-sm text-white">{r.name}</span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${r.completed ? "bg-emerald-400/15 text-emerald-300" : "bg-amber-400/15 text-amber-300"}`}>
                  {r.completed ? "Assessment done · rewarded" : "Pending assessment"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const cmap = {
  cyan: "text-cyan-300 border-cyan-400/20 bg-cyan-400/5",
  amber: "text-amber-300 border-amber-400/20 bg-amber-400/5",
  emerald: "text-emerald-300 border-emerald-400/20 bg-emerald-400/5",
};
const Stat = ({ icon: Icon, label, value, color, caps, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-white/8 bg-gradient-to-br from-[#131C2E] to-[#0F1626] p-5">
    <div className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl border ${cmap[color]}`}>
      <Icon className="h-5 w-5" />
    </div>
    <p className={`font-display text-2xl font-extrabold text-white ${caps ? "capitalize" : ""}`}>{value}</p>
    <p className="text-xs text-slate-400 mt-0.5">{label}</p>
  </div>
);
